import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Phone, Shield, Camera, Users, Mail, MapPin, Send, Loader2, CheckCircle, Zap, Star } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useLang } from '../../i18n';
import { usePWAInstall } from '../../hooks/usePWAInstall';
import { SERVICE_ICONS, DEFAULT_SERVICES } from '../../utils/constants';
import { Logo } from '../common/Logo';
import { LanguageSwitcher } from '../common/LanguageSwitcher';
import { InstallModal } from '../common/InstallModal';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const ContactForm = () => {
  const [form, setForm] = useState({ first_name: '', last_name: '', email: '', phone: '', message: '' });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.first_name || !form.email || !form.message) {
      toast.error('Please fill in all required fields');
      return;
    }
    setSending(true);
    try {
      const res = await fetch(`${API_URL}/api/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: `${form.first_name} ${form.last_name}`.trim(),
          email: form.email,
          phone: form.phone,
          subject: `New Contact from ${form.first_name} ${form.last_name}`,
          message: form.message,
          property_type: 'general'
        })
      });
      const data = await res.json();
      if (res.ok) {
        setSent(true);
        toast.success('Message sent successfully!');
        setForm({ first_name: '', last_name: '', email: '', phone: '', message: '' });
        setTimeout(() => setSent(false), 5000);
      } else {
        toast.error(data.detail || 'Failed to send message');
      }
    } catch {
      toast.error('Network error. Please try again.');
    } finally {
      setSending(false);
    }
  };

  if (sent) {
    return (
      <div className="bg-gray-900/60 border border-gray-800 rounded-2xl p-10 flex flex-col items-center justify-center min-h-[460px]">
        <div className="w-16 h-16 rounded-full bg-green-500/10 border border-green-500/30 flex items-center justify-center mb-6">
          <CheckCircle className="w-8 h-8 text-green-400" />
        </div>
        <h3 className="text-2xl font-bold text-white mb-2">Message Sent!</h3>
        <p className="text-gray-400 text-center">Thank you for contacting us. We'll get back to you within 24 hours.</p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="bg-gray-900/60 border border-gray-800 rounded-2xl p-8 md:p-10" data-testid="contact-form">
      <h3 className="text-2xl font-bold text-white mb-2">Send Us a Message</h3>
      <p className="text-sm text-gray-500 mb-8">We'll respond within 24 hours</p>

      <div className="grid sm:grid-cols-2 gap-5 mb-5">
        <div>
          <label className="block text-xs text-gray-500 uppercase tracking-wider mb-2">First Name *</label>
          <input
            type="text"
            value={form.first_name}
            onChange={e => setForm({...form, first_name: e.target.value})}
            className="w-full bg-black/50 border border-gray-700 rounded-xl px-4 py-3 text-white placeholder-gray-600 focus:border-red-500 focus:ring-1 focus:ring-red-500/30 outline-none transition-all"
            placeholder="John"
            required
            data-testid="contact-first-name"
          />
        </div>
        <div>
          <label className="block text-xs text-gray-500 uppercase tracking-wider mb-2">Last Name</label>
          <input
            type="text"
            value={form.last_name}
            onChange={e => setForm({...form, last_name: e.target.value})}
            className="w-full bg-black/50 border border-gray-700 rounded-xl px-4 py-3 text-white placeholder-gray-600 focus:border-red-500 focus:ring-1 focus:ring-red-500/30 outline-none transition-all"
            placeholder="Doe"
            data-testid="contact-last-name"
          />
        </div>
      </div>

      <div className="grid sm:grid-cols-2 gap-5 mb-5">
        <div>
          <label className="block text-xs text-gray-500 uppercase tracking-wider mb-2">Email *</label>
          <input
            type="email"
            value={form.email}
            onChange={e => setForm({...form, email: e.target.value})}
            className="w-full bg-black/50 border border-gray-700 rounded-xl px-4 py-3 text-white placeholder-gray-600 focus:border-red-500 focus:ring-1 focus:ring-red-500/30 outline-none transition-all"
            placeholder="john@example.com"
            required
            data-testid="contact-email"
          />
        </div>
        <div>
          <label className="block text-xs text-gray-500 uppercase tracking-wider mb-2">Phone Number</label>
          <input
            type="tel"
            value={form.phone}
            onChange={e => setForm({...form, phone: e.target.value})}
            className="w-full bg-black/50 border border-gray-700 rounded-xl px-4 py-3 text-white placeholder-gray-600 focus:border-red-500 focus:ring-1 focus:ring-red-500/30 outline-none transition-all"
            placeholder="(203) 555-1234"
            data-testid="contact-phone"
          />
        </div>
      </div>

      <div className="mb-6">
        <label className="block text-xs text-gray-500 uppercase tracking-wider mb-2">Description *</label>
        <textarea
          value={form.message}
          onChange={e => setForm({...form, message: e.target.value})}
          rows={4}
          className="w-full bg-black/50 border border-gray-700 rounded-xl px-4 py-3 text-white placeholder-gray-600 focus:border-red-500 focus:ring-1 focus:ring-red-500/30 outline-none transition-all resize-none"
          placeholder="Describe what you need help with..."
          required
          data-testid="contact-message"
        />
      </div>

      <button
        type="submit"
        disabled={sending}
        className="w-full flex items-center justify-center gap-3 py-4 bg-red-600 hover:bg-red-700 disabled:bg-red-600/50 text-white font-bold text-lg rounded-xl transition-all duration-200 shadow-lg shadow-red-600/20"
        data-testid="contact-send-btn"
      >
        {sending ? (
          <><Loader2 className="w-5 h-5 animate-spin" /> Sending...</>
        ) : (
          <><Send className="w-5 h-5" /> Send Message</>
        )}
      </button>
    </form>
  );
};

const LandingPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t } = useLang();
  const [siteContent, setSiteContent] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [googleMapsUrl, setGoogleMapsUrl] = useState('https://maps.google.com/?cid=1339169573033624960');
  const [writeReviewUrl, setWriteReviewUrl] = useState('https://g.page/r/CT0jeAgUi6VPEAI/review');
  const [googleRating, setGoogleRating] = useState(5.0);
  const [totalReviews, setTotalReviews] = useState(0);
  const pwa = usePWAInstall();

  useEffect(() => {
    if (user) {
      navigate(`/${user.role}`);
    }
  }, [user, navigate]);

  useEffect(() => {
    fetch(`${API_URL}/api/site-content`)
      .then(r => r.json())
      .then(data => setSiteContent(data))
      .catch(() => {});
    fetch(`${API_URL}/api/reviews`)
      .then(r => r.json())
      .then(data => {
        setReviews(data.reviews || data || []);
        if (data.google_maps_url) setGoogleMapsUrl(data.google_maps_url);
        if (data.write_review_url) setWriteReviewUrl(data.write_review_url);
        if (data.rating) setGoogleRating(data.rating);
        if (data.total_reviews) setTotalReviews(data.total_reviews);
      })
      .catch(() => {});
  }, []);

  const hero = siteContent?.hero || { title_line1: "NEW YORK CITY'S PREMIER", title_line2: "ELECTRICAL & SECURITY", description: "High Voltage - Low Voltage - CCTV — Enterprise electrical and security solutions for commercial and residential properties.", cta_button: "Request Service", secondary_button: "Our Services" };
  const company = siteContent?.company || { phone: "(212) 555-ELEC", tagline: "Licensed & Insured | NYC DOB Certified", copyright: "BELLAJA Electrical & Security Systems. All rights reserved." };
  const servicesData = siteContent?.services || DEFAULT_SERVICES;
  const ctaSection = siteContent?.cta_section || { title: "READY TO GET STARTED?", description: "Create your account to request services, get quotes, and track your projects.", button_text: "Create Your Account" };

  const services = Object.entries(servicesData).map(([id, s]) => ({ id, ...s, icon: SERVICE_ICONS[id] || Shield }));

  return (
    <div className="min-h-screen bg-black">
      <header className="bg-black border-b border-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-24">
            <Logo size="xlarge" />
            <div className="flex items-center gap-3">
              <nav className="hidden lg:flex items-center gap-6 text-sm text-white mr-4">
                <a href="#services" className="hover:text-red-400 transition-colors">Services</a>
                <a href="#reviews" className="hover:text-red-400 transition-colors">Reviews</a>
                <a href="#contact" className="hover:text-red-400 transition-colors">Contact</a>
                <span className="flex items-center gap-2">
                  <Phone className="w-4 h-4 text-red-500" />
                  <a href="tel:2038037035" className="hover:text-red-400 transition-colors">(203) 803-7035</a>
                </span>
              </nav>
              <LanguageSwitcher compact />
              <button 
                onClick={() => navigate('/quote')}
                className="hidden sm:flex items-center gap-2 text-sm px-4 py-2 bg-red-600/10 text-red-400 border border-red-500/30 font-bold rounded hover:bg-red-600/20 transition-all"
                data-testid="header-quote-btn"
              >
                {t('nav_get_quote')}
              </button>
              <button 
                onClick={() => navigate('/login')}
                className="btn-primary text-sm px-5 py-2"
                data-testid="header-login-btn"
              >
                {t('nav_customer_login')}
              </button>
              <button 
                onClick={() => window.location.href = '/staff'}
                className="flex items-center gap-2 text-sm px-5 py-2 bg-white text-black font-bold rounded hover:bg-gray-200 transition-all"
                data-testid="header-team-btn"
              >
                <Users className="w-4 h-4" />
                {t('nav_team')}
              </button>
            </div>
          </div>
        </div>
      </header>

      <section className="relative bg-black overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-black via-gray-900 to-black"></div>
        <div className="absolute inset-0 overflow-hidden">
          <svg className="absolute inset-0 w-full h-full opacity-[0.06]" xmlns="http://www.w3.org/2000/svg">
            <defs>
              <pattern id="grid" width="60" height="60" patternUnits="userSpaceOnUse">
                <path d="M 60 0 L 0 0 0 60" fill="none" stroke="rgba(220,38,38,0.3)" strokeWidth="0.5"/>
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#grid)" />
          </svg>
          <div className="absolute top-1/4 left-1/4 w-1 h-32 bg-gradient-to-b from-transparent via-red-500 to-transparent opacity-20 animate-pulse" style={{animationDuration:'3s'}}></div>
          <div className="absolute top-1/3 right-1/3 w-1 h-48 bg-gradient-to-b from-transparent via-red-500 to-transparent opacity-15 animate-pulse" style={{animationDuration:'4s',animationDelay:'1s'}}></div>
          <div className="absolute bottom-1/4 left-2/3 w-1 h-24 bg-gradient-to-b from-transparent via-red-400 to-transparent opacity-20 animate-pulse" style={{animationDuration:'2.5s',animationDelay:'0.5s'}}></div>
          <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-red-500/30 to-transparent" style={{animation:'scanDown 6s ease-in-out infinite'}}></div>
        </div>
        <style>{`@keyframes scanDown { 0%,100%{transform:translateY(0)} 50%{transform:translateY(500px)} }`}</style>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-3xl"
          >
            <div className="inline-flex items-center gap-2 px-3 py-1 bg-red-500/10 border border-red-500/20 rounded-full text-xs text-red-400 font-medium mb-6">
              <Shield className="w-3.5 h-3.5" />
              {t('hero_badge')}
            </div>
            <h1 className="font-heading text-5xl md:text-7xl font-black text-white mb-6 leading-tight">
              {hero.title_line1}<br />
              <span className="text-red-500">{hero.title_line2}</span>
            </h1>
            <p className="text-xl text-gray-400 mb-8 max-w-2xl">
              {t('hero_subtitle')}
            </p>
            <div className="flex flex-wrap gap-4">
              <button 
                onClick={() => navigate('/quote')}
                className="btn-primary text-lg px-8 py-4"
                data-testid="hero-request-service-btn"
              >
                {t('hero_cta')}
              </button>
              <button 
                onClick={() => document.getElementById('services').scrollIntoView({ behavior: 'smooth' })}
                className="btn-secondary text-lg px-8 py-4"
                data-testid="hero-view-services-btn"
              >
                {t('hero_cta2')}
              </button>
            </div>
            <p className="text-xs text-gray-600 mt-6">{t('hero_trusted')}</p>
          </motion.div>
        </div>
      </section>

      <section id="services" className="py-20 bg-gray-950">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="font-heading text-4xl md:text-5xl font-bold text-white mb-4">
              {t('section_services').toUpperCase()}
            </h2>
            <p className="text-lg text-gray-400 max-w-2xl mx-auto">
              {t('section_services_sub')}
            </p>
          </motion.div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((service, index) => {
              const Icon = service.icon;
              return (
                <motion.div
                  key={service.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="group rounded-xl overflow-hidden bg-gray-900 border border-gray-800 hover:border-red-500/50 transition-all duration-300"
                  data-testid={`service-card-${service.id}`}
                >
                  <div className="relative h-48 overflow-hidden">
                    <img
                      src={service.image || `https://images.unsplash.com/photo-1585743876840-53866e12a598?w=600&q=80`}
                      alt={service.name}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-gray-900 via-gray-900/60 to-transparent"></div>
                    <div className="absolute top-3 right-3 bg-black/70 backdrop-blur-sm px-2.5 py-1 rounded flex items-center gap-1.5">
                      <div className="w-4 h-4 bg-red-600 flex items-center justify-center" style={{fontSize:'6px',fontWeight:'900',color:'#fff'}}>B</div>
                      <span className="text-[10px] font-bold text-white tracking-wider">BELLAJA</span>
                    </div>
                    <div className="absolute bottom-3 left-4">
                      <div className="w-12 h-12 bg-red-600 rounded-lg flex items-center justify-center shadow-lg shadow-red-600/30">
                        <Icon className="w-6 h-6 text-white" />
                      </div>
                    </div>
                  </div>

                  <div className="p-5">
                    <h3 className="font-heading text-xl font-bold text-white mb-1">
                      {service.name}
                    </h3>
                    <p className="text-sm text-red-400 font-medium mb-2">{service.desc}</p>
                    
                    {service.description && (
                      <p className="text-xs text-gray-400 mb-3 leading-relaxed line-clamp-3">
                        {service.description}
                      </p>
                    )}
                    
                    <div className="mb-4">
                      <div className="flex flex-wrap gap-1.5">
                        {(service.jobs || []).slice(0, 4).map((job) => (
                          <span key={job} className="text-[11px] bg-gray-800 text-gray-300 px-2 py-0.5 rounded">
                            {job}
                          </span>
                        ))}
                        {(service.jobs || []).length > 4 && (
                          <span className="text-[11px] bg-red-500/10 text-red-400 px-2 py-0.5 rounded">
                            +{service.jobs.length - 4} more
                          </span>
                        )}
                      </div>
                    </div>

                    <button
                      onClick={() => navigate(`/gallery/${service.id}`)}
                      className="w-full flex items-center justify-center gap-2 py-2.5 bg-red-600/10 border border-red-500/20 text-red-400 hover:bg-red-600/20 hover:border-red-500/40 transition-all duration-200 text-sm font-medium rounded-lg"
                      data-testid={`gallery-btn-${service.id}`}
                    >
                      <Camera className="w-4 h-4" />
                      {t('view_gallery')}
                    </button>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* SEO Rich Content - Service Areas */}
      <section className="py-16 bg-black border-t border-gray-800/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-14 h-14 rounded-xl bg-red-600/10 border border-red-500/20 flex items-center justify-center mx-auto mb-4">
                <Zap className="w-7 h-7 text-red-500" />
              </div>
              <h3 className="text-lg font-bold text-white mb-2">High & Low Voltage Experts</h3>
              <p className="text-sm text-gray-400">Residential & commercial electrical installations, panel upgrades, EV chargers, LED lighting, and emergency repairs across NYC & Connecticut.</p>
            </div>
            <div className="text-center">
              <div className="w-14 h-14 rounded-xl bg-red-600/10 border border-red-500/20 flex items-center justify-center mx-auto mb-4">
                <Shield className="w-7 h-7 text-red-500" />
              </div>
              <h3 className="text-lg font-bold text-white mb-2">Security & CCTV Specialists</h3>
              <p className="text-sm text-gray-400">Professional CCTV camera installation, DVR/NVR systems, access control, smart locks, and intercom systems for homes and businesses.</p>
            </div>
            <div className="text-center">
              <div className="w-14 h-14 rounded-xl bg-red-600/10 border border-red-500/20 flex items-center justify-center mx-auto mb-4">
                <MapPin className="w-7 h-7 text-red-500" />
              </div>
              <h3 className="text-lg font-bold text-white mb-2">Serving NYC & Connecticut</h3>
              <p className="text-sm text-gray-400">Manhattan, Brooklyn, Queens, Bronx, Staten Island, Stamford, Bridgeport, New Haven, Hartford, Norwalk, Danbury & all surrounding areas.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Google Reviews Section */}
      <section id="reviews" className="py-20 bg-gray-950">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-16"
            >
              <div className="flex items-center justify-center gap-3 mb-4">
                <svg viewBox="0 0 24 24" className="w-8 h-8" xmlns="http://www.w3.org/2000/svg">
                  <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
                  <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                  <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                  <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                </svg>
                <h2 className="font-heading text-4xl md:text-5xl font-bold text-white">
                  GOOGLE REVIEWS
                </h2>
              </div>
              <div className="flex items-center justify-center gap-2 mb-3">
                {[1,2,3,4,5].map(i => <Star key={i} className="w-6 h-6 text-yellow-500 fill-yellow-500" />)}
                <span className="text-xl font-bold text-white ml-2">{googleRating.toFixed(1)}</span>
                {totalReviews > 0 && <span className="text-gray-400 ml-1">({totalReviews} reviews)</span>}
              </div>
              <p className="text-gray-400">See what our customers say about BELLAJA Electrical & Security Systems</p>
              <a
                href={googleMapsUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 mt-3 text-sm text-blue-400 hover:text-blue-300 transition-colors"
              >
                View our Google Business Profile
              </a>
            </motion.div>

            {reviews.length > 0 ? (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
                {reviews.slice(0, 6).map((review, idx) => (
                <motion.div
                  key={review.id || idx}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: idx * 0.1 }}
                  className="bg-gray-900/60 border border-gray-800 rounded-2xl p-6 hover:border-gray-700 transition-all"
                  data-testid={`review-card-${idx}`}
                >
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 rounded-full bg-red-600/20 border border-red-500/30 flex items-center justify-center">
                      <span className="text-red-400 font-bold text-sm">{review.name?.charAt(0)}</span>
                    </div>
                    <div>
                      <p className="text-white font-semibold text-sm">{review.name}</p>
                      <div className="flex items-center gap-1">
                        {Array.from({length: review.rating}, (_, i) => (
                          <Star key={i} className="w-3.5 h-3.5 text-yellow-500 fill-yellow-500" />
                        ))}
                        {review.source === 'google' && (
                          <svg viewBox="0 0 24 24" className="w-3.5 h-3.5 ml-1" xmlns="http://www.w3.org/2000/svg">
                            <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
                            <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                            <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                            <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                          </svg>
                        )}
                      </div>
                    </div>
                  </div>
                  <p className="text-gray-300 text-sm leading-relaxed">"{review.text}"</p>
                </motion.div>
              ))}
              </div>
            ) : (
              <div className="text-center py-12 mb-12">
                <div className="w-16 h-16 rounded-full bg-gray-800 border border-gray-700 flex items-center justify-center mx-auto mb-4">
                  <Star className="w-8 h-8 text-yellow-500" />
                </div>
                <p className="text-gray-400 text-lg mb-2">Be the first to review us on Google!</p>
                <p className="text-gray-500 text-sm">Your feedback helps us serve you better</p>
              </div>
            )}

            <div className="text-center">
              <a
                href={writeReviewUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-3 px-8 py-4 bg-white text-gray-900 font-bold text-lg rounded-xl hover:bg-gray-100 transition-all shadow-lg"
                data-testid="google-review-btn"
              >
                <svg viewBox="0 0 24 24" className="w-6 h-6" xmlns="http://www.w3.org/2000/svg">
                  <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
                  <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                  <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                  <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                </svg>
                Leave Us a Google Review
              </a>
            </div>
          </div>
        </section>

      {/* FAQ Section - Shows in Google Rich Results */}
      <section id="faq" className="py-20 bg-black border-t border-gray-800/50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-12">
            <h2 className="font-heading text-4xl md:text-5xl font-bold text-white mb-4">FREQUENTLY ASKED QUESTIONS</h2>
            <div className="w-20 h-1 bg-red-600 mx-auto"></div>
          </motion.div>
          <div className="space-y-4" data-testid="faq-section">
            {[
              { q: "What areas does BELLAJA Electrical serve?", a: "We serve all 5 boroughs of NYC (Manhattan, Brooklyn, Queens, Bronx, Staten Island), Westchester County (Yonkers, White Plains, New Rochelle), Northern New Jersey (Jersey City, Newark, Hoboken), Long Island, and all of Connecticut including Stamford, Bridgeport, New Haven, Hartford, Norwalk, Danbury, Waterbury, Greenwich, Fairfield - a full 30+ mile radius!" },
              { q: "Do you offer 24/7 emergency electrical services?", a: "Yes! Call (203) 803-7035 anytime for immediate emergency electrical assistance across New York City and Connecticut. We respond fast to power outages, electrical fires, and urgent repairs." },
              { q: "What types of CCTV cameras do you install?", a: "We install all types: IP cameras, analog CCTV, dome, bullet, PTZ, 4K/HD, and wireless cameras. Complete DVR and NVR systems for homes, apartments, offices, retail stores, and commercial buildings." },
              { q: "Do you handle high voltage and low voltage work?", a: "Yes! High voltage: panel upgrades, circuit breakers, commercial/industrial wiring. Low voltage: CCTV cameras, network cabling, intercoms, access control, fire alarms, and smart home systems." },
              { q: "What is your service radius?", a: "We cover a 30+ mile radius from NYC - all five boroughs, Westchester, Long Island, Northern NJ, and all of Connecticut from Greenwich to Hartford and beyond." },
              { q: "Do you install access control and smart locks?", a: "Yes! Smart locks, keyless entry, card readers, biometric fingerprint scanners, video intercom, and complete building access control systems for apartments, offices, and commercial properties." }
            ].map((faq, idx) => (
              <motion.details
                key={idx}
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.05 }}
                className="group bg-gray-900/60 border border-gray-800 rounded-xl overflow-hidden"
              >
                <summary className="flex items-center justify-between px-6 py-5 cursor-pointer text-white font-semibold hover:bg-gray-800/50 transition-colors list-none">
                  <span className="pr-4">{faq.q}</span>
                  <span className="text-red-500 text-2xl font-light group-open:rotate-45 transition-transform">+</span>
                </summary>
                <div className="px-6 pb-5 text-gray-400 leading-relaxed">{faq.a}</div>
              </motion.details>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 bg-black">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-heading text-4xl md:text-5xl font-bold text-white mb-4">
              {ctaSection.title}
            </h2>
            <p className="text-lg text-gray-400 mb-8 max-w-2xl mx-auto">
              {ctaSection.description}
            </p>
            <button 
              onClick={() => navigate('/login')}
              className="btn-primary text-lg px-10 py-4"
              data-testid="cta-create-account-btn"
            >
              {ctaSection.button_text}
            </button>
          </motion.div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-20 bg-gradient-to-b from-black via-gray-950 to-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="font-heading text-4xl md:text-5xl font-bold text-white mb-4">
              CONTACT US
            </h2>
            <div className="w-20 h-1 bg-red-600 mx-auto mb-4"></div>
            <p className="text-lg text-gray-400 max-w-2xl mx-auto">
              Get in touch for a free consultation. We serve New York City, Connecticut & surrounding areas.
            </p>
          </motion.div>

          <div className="grid lg:grid-cols-5 gap-12">
            {/* Left side - Company Info */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="lg:col-span-2 flex flex-col gap-8"
            >
              <div className="bg-gray-900/60 border border-gray-800 rounded-2xl p-8">
                <div className="mb-8">
                  <Logo size="xlarge" />
                </div>
                <div className="space-y-6">
                  <a href="tel:2038037035" className="flex items-center gap-4 group" data-testid="contact-phone-link">
                    <div className="w-12 h-12 rounded-xl bg-red-600/10 border border-red-500/20 flex items-center justify-center group-hover:bg-red-600/20 transition-all">
                      <Phone className="w-5 h-5 text-red-500" />
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 uppercase tracking-wider">Call Us 24/7</p>
                      <p className="text-lg font-bold text-white group-hover:text-red-400 transition-colors">(203) 803-7035</p>
                    </div>
                  </a>

                  <a href="mailto:info@bellajaelectric.com" className="flex items-center gap-4 group" data-testid="contact-email-link">
                    <div className="w-12 h-12 rounded-xl bg-red-600/10 border border-red-500/20 flex items-center justify-center group-hover:bg-red-600/20 transition-all">
                      <Mail className="w-5 h-5 text-red-500" />
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 uppercase tracking-wider">Email Us</p>
                      <p className="text-lg font-bold text-white group-hover:text-red-400 transition-colors">info@bellajaelectric.com</p>
                    </div>
                  </a>

                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-xl bg-red-600/10 border border-red-500/20 flex items-center justify-center">
                      <MapPin className="w-5 h-5 text-red-500" />
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 uppercase tracking-wider">Service Areas</p>
                      <p className="text-sm font-semibold text-white">New York City & Connecticut</p>
                    </div>
                  </div>
                </div>

                <div className="mt-8 pt-6 border-t border-gray-800">
                  <div className="flex items-center gap-2 mb-3">
                    <Zap className="w-4 h-4 text-red-500" />
                    <span className="text-sm font-semibold text-white">Licensed & Insured</span>
                  </div>
                  <div className="flex gap-1">
                    {[1,2,3,4,5].map(i => <Star key={i} className="w-4 h-4 text-yellow-500 fill-yellow-500" />)}
                    <span className="text-xs text-gray-400 ml-2">5.0 on Google</span>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Right side - Contact Form */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="lg:col-span-3"
            >
              <ContactForm />
            </motion.div>
          </div>
        </div>
      </section>

      <footer className="bg-black border-t border-gray-800 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <Logo />
            <div className="flex items-center gap-6">
              <a href="tel:2038037035" className="text-gray-400 hover:text-red-400 transition-colors text-sm flex items-center gap-2">
                <Phone className="w-4 h-4" /> (203) 803-7035
              </a>
              <a href="mailto:info@bellajaelectric.com" className="text-gray-400 hover:text-red-400 transition-colors text-sm flex items-center gap-2">
                <Mail className="w-4 h-4" /> info@bellajaelectric.com
              </a>
              <LanguageSwitcher />
            </div>
            <p className="text-sm text-gray-500">&copy; {new Date().getFullYear()} BELLAJA Electrical & Security Systems.</p>
          </div>
        </div>
      </footer>

      <InstallModal pwa={pwa} />
    </div>
  );
};

export default LandingPage;
